import { Link } from 'react-router-dom';

import { Check, LogIn, Shield } from 'lucide-react';

export interface CaptainForm {
  licenseNumber: string;
  lastName: string;
  firstName: string;
  email: string;
}

interface CaptainSectionProps {
  isLoggedIn: boolean;
  loginUrl: string;
  restrictedToMembers: boolean;
  captainData: CaptainForm;
  /** Demande client #4 — nom/prénom du capitaine administrables même connecté */
  captainName: { firstName: string; lastName: string };
  onCaptainNameChange: (next: { firstName: string; lastName: string }) => void;
  captainManual: CaptainForm;
  onCaptainManualChange: (next: CaptainForm) => void;
  captainPhone: string;
  onCaptainPhoneChange: (next: string) => void;
  /** Licence saisie par le capitaine connecté (peut différer du profil) */
  captainLicense: string;
  onCaptainLicenseChange: (license: string) => void;
  /** Licence déjà utilisée par un autre joueur */
  duplicateLicense?: boolean;
  /** Auto-déclaration sur l'honneur du capitaine */
  licenseAutoDeclared: boolean;
  onLicenseAutoDeclaredChange: (value: boolean) => void;
}

const inputCls =
  'w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all';
const inputReadOnlyCls = `${inputCls} cursor-not-allowed bg-slate-100 text-slate-500`;

export default function CaptainSection({
  isLoggedIn,
  loginUrl,
  restrictedToMembers,
  captainData,
  captainName,
  onCaptainNameChange,
  captainManual,
  onCaptainManualChange,
  captainPhone,
  onCaptainPhoneChange,
  captainLicense,
  onCaptainLicenseChange,
  duplicateLicense = false,
  licenseAutoDeclared,
  onLicenseAutoDeclaredChange,
}: CaptainSectionProps) {
  // Bloc d'auto-déclaration sur l'honneur (concours réservés aux licenciés).
  // BoulesConnect ne vérifie PAS la licence : déclaration, pas validation fédérale.
  const declarationBlock = restrictedToMembers && (
    <div className='rounded-2xl border border-slate-200 bg-slate-50 p-4'>
      <p className='text-xs text-slate-500 mb-3'>
        Numéro renseigné par le joueur. BoulesConnect ne vérifie pas automatiquement la validité de la licence. Le
        capitaine déclare sur l&apos;honneur être titulaire d&apos;une licence valide et non suspendue.
      </p>
      <label className='flex items-start gap-3 cursor-pointer group'>
        <button
          type='button'
          onClick={() => onLicenseAutoDeclaredChange(!licenseAutoDeclared)}
          className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
            licenseAutoDeclared ? 'bg-blue-600 border-blue-600' : 'border-slate-300 group-hover:border-blue-400'
          }`}
          aria-pressed={licenseAutoDeclared}
        >
          {licenseAutoDeclared && <Check className='w-3 h-3 text-white' />}
        </button>
        <span className='text-xs text-slate-600'>
          Je déclare sur l&apos;honneur être titulaire de cette licence FFPJP, qu&apos;elle est valide pour la saison en
          cours et qu&apos;elle n&apos;est pas sous le coup d&apos;une suspension.
        </span>
      </label>
    </div>
  );

  return (
    <div className='bg-white rounded-3xl border border-slate-100 shadow-sm p-8'>
      <div className='flex items-center gap-3 mb-6'>
        <div className='w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center'>
          <Shield className='w-5 h-5 text-blue-600' />
        </div>
        <div>
          <h2 className='text-xl font-extrabold text-slate-900'>Joueur 1 — Capitaine</h2>
          <p className='text-sm text-slate-500'>
            {isLoggedIn
              ? 'Pré-rempli depuis votre compte — nom et prénom modifiables pour cette inscription'
              : 'Connectez-vous pour pré-remplir automatiquement'}
          </p>
        </div>
      </div>

      {!isLoggedIn && (
        <div className='mb-6 flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4'>
          <LogIn className='mt-0.5 h-5 w-5 shrink-0 text-blue-500' />
          <p className='text-sm text-blue-700'>
            <Link to={loginUrl} className='font-bold underline hover:text-blue-900'>
              Connectez-vous
            </Link>{' '}
            avec votre compte joueur pour pré-remplir automatiquement vos informations et valider votre inscription.
          </p>
        </div>
      )}

      <div className='space-y-5'>
        {isLoggedIn ? (
          <>
            <div className={`grid ${restrictedToMembers ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-5`}>
              {restrictedToMembers && (
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-2'>
                    N° de licence FFPJP <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    inputMode='numeric'
                    value={captainLicense}
                    onChange={e => onCaptainLicenseChange(e.target.value.replace(/\D/g, ''))}
                    placeholder='Ex : 06912345'
                    className={inputCls}
                    required
                    maxLength={20}
                  />
                  {duplicateLicense && (
                    <p className='text-xs text-red-600 mt-1 font-medium'>
                      Un même numéro de licence ne peut pas être utilisé deux fois dans la même équipe.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  Nom <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  value={captainName.lastName}
                  onChange={e => onCaptainNameChange({ ...captainName, lastName: e.target.value })}
                  placeholder='DUPONT'
                  className={inputCls}
                  maxLength={50}
                />
              </div>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  Prénom <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  value={captainName.firstName}
                  onChange={e => onCaptainNameChange({ ...captainName, firstName: e.target.value })}
                  placeholder='Jean'
                  className={inputCls}
                  maxLength={50}
                />
              </div>
            </div>
            <div className='grid md:grid-cols-2 gap-5'>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>Email</label>
                <div className={inputReadOnlyCls}>{captainData.email}</div>
              </div>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  Téléphone <span className='text-slate-400 font-normal'>(contact organisateur)</span>
                </label>
                <input
                  type='tel'
                  value={captainPhone}
                  onChange={e => onCaptainPhoneChange(e.target.value)}
                  placeholder='06 12 34 56 78'
                  className={inputCls}
                  maxLength={15}
                />
              </div>
            </div>
            {declarationBlock}
          </>
        ) : (
          <>
            <div className={`grid ${restrictedToMembers ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-5`}>
              {restrictedToMembers && (
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-2'>
                    N° de licence FFPJP <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    value={captainManual.licenseNumber}
                    inputMode='numeric'
                    onChange={e =>
                      onCaptainManualChange({ ...captainManual, licenseNumber: e.target.value.replace(/\D/g, '') })
                    }
                    placeholder='Ex : 06912345'
                    className={inputCls}
                    required
                    maxLength={20}
                  />
                  {duplicateLicense && (
                    <p className='text-xs text-red-600 mt-1 font-medium'>
                      Un même numéro de licence ne peut pas être utilisé deux fois dans la même équipe.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  Nom <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  value={captainManual.lastName}
                  onChange={e => onCaptainManualChange({ ...captainManual, lastName: e.target.value })}
                  placeholder='DUPONT'
                  className={inputCls}
                />
              </div>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  Prénom <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  value={captainManual.firstName}
                  onChange={e => onCaptainManualChange({ ...captainManual, firstName: e.target.value })}
                  placeholder='Jean'
                  className={inputCls}
                />
              </div>
            </div>
            <div className='grid md:grid-cols-2 gap-5'>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  Email <span className='text-red-500'>*</span>
                </label>
                <input
                  type='email'
                  value={captainManual.email}
                  onChange={e => onCaptainManualChange({ ...captainManual, email: e.target.value })}
                  placeholder='jean.dupont@email.fr'
                  className={inputCls}
                />
              </div>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  Téléphone <span className='text-slate-400 font-normal'>(contact organisateur)</span>
                </label>
                <input
                  type='tel'
                  value={captainPhone}
                  onChange={e => onCaptainPhoneChange(e.target.value)}
                  placeholder='06 12 34 56 78'
                  className={inputCls}
                  maxLength={15}
                />
              </div>
            </div>
            {declarationBlock}
          </>
        )}
      </div>
    </div>
  );
}
